import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import { updateDoc, setDoc } from '@angular/fire/firestore';
import { BookingLockService } from 'src/app/shared/services/booking-lock.service';
import { UploadDocumentDialogComponent } from '../upload-document-dialog/upload-document-dialog.component';
import { RealtimeBookingService } from 'src/app/shared/services/realtime-booking.service';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';

// Add a simple UX interface for snacks/overlays
interface UXService {
  openSnackBar(msg: string, action: string): void;
  showOverlay(msg: string): void;
  closeOverlay(): void;
}

export interface InvoiceTemplateData {
  bookingId: string;
  booking: any;
}

@Component({
  selector: 'app-invoice-template-dialog',
  templateUrl: './invoice-template-dialog.component.html',
  styleUrls: ['./invoice-template-dialog.component.scss'],
})
export class InvoiceTemplateDialogComponent implements OnInit {
  isLoading = true;
  timesheetRows: any[] = [];
  vendorBranding: any = {};
  customerBranding: any = {};
  vendorVerificationHours: number | null = null;
  isFallbackToCustomer = false;
  invoiceNumber: string = '';
  today = new Date();

  // Signature Logic
  @ViewChild('pdfPage1') pdfPage1!: ElementRef;
  @ViewChild('sigCanvas') sigCanvas!: ElementRef<HTMLCanvasElement>;
  signatureUrl: string = '';
  isDrawing = false;
  ctx!: CanvasRenderingContext2D;
  showSignatureSection = false;
  isFinalized = false;
  isPdfGenerating = false;
  vendorLogoBase64: string | null = null;
  signatureBase64: string | null = null;
  deliveryCharge: number = 0;
  discount: number = 0;

  // Uploaded Documents
  uploadedTimesheetUrl: string = '';
  uploadedInvoiceUrl: string = '';

  private rtSub?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<InvoiceTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InvoiceTemplateData,
    private afs: Firestore,
    private auth: Auth,
    private bookingLock: BookingLockService,
    private dialog: MatDialog,
    private rtBooking: RealtimeBookingService,
    private notificationService: NotificationService,
    private ux: UxserviceService,
    private cdr: ChangeDetectorRef
  ) {}

  get isVendor(): boolean {
    const vendorId = this.data.booking?.equipment?.bywhom;
    return !!vendorId && this.auth.currentUser?.uid === vendorId;
  }

  get canEditInvoice(): boolean {
    return this.isVendor && !this.isFinalized;
  }

  ngOnInit(): void {
    this.invoiceNumber =
      'INV-' + this.data.bookingId.substring(0, 6).toUpperCase();
    this.isFinalized = this.data.booking.invoiceFinalized || false;
    this.signatureUrl = this.data.booking.vendorSignatureUrl || '';
    this.signatureBase64 = this.data.booking.vendorSignatureBase64 || null; // Added
    this.uploadedTimesheetUrl = this.data.booking.uploadedTimesheetUrl || '';
    this.uploadedInvoiceUrl = this.data.booking.uploadedInvoiceUrl || '';
    this.deliveryCharge = this.data.booking.deliveryCharge || 0;
    this.discount = this.data.booking.discount || 0;
    this.loadData();
    this.initRealtimeSync();
  }

  initRealtimeSync() {
    this.rtSub = this.rtBooking
      .watchBooking(this.data.bookingId)
      .subscribe((rtData: any) => {
        if (rtData) {
          if (rtData.invoice) {
            const inv = rtData.invoice;
            if (inv.finalized !== undefined) this.isFinalized = inv.finalized;
            if (inv.url) this.uploadedInvoiceUrl = inv.url;
            if (inv.vendorSig) this.signatureUrl = inv.vendorSig;
            if (inv.vendorSigBase64) this.signatureBase64 = inv.vendorSigBase64;
            if (inv.invoicedAt) {
              if (!this.data.booking) this.data.booking = {};
              this.data.booking.invoicedAt = { toDate: () => new Date(inv.invoicedAt) };
            }
          }
          if (rtData.timesheet) {
            const ts = rtData.timesheet;
            if (ts.rows) {
              const vendorRows = ts.rows.filter(
                (r: any) => (r.addedBy || 'Vendor') === 'Vendor',
              );
              const customerRows = ts.rows.filter(
                (r: any) => (r.addedBy || 'Vendor') === 'Customer',
              );
              if (vendorRows.length > 0) {
                this.timesheetRows = vendorRows;
                this.isFallbackToCustomer = false;
              } else if (customerRows.length > 0) {
                this.timesheetRows = customerRows;
                this.isFallbackToCustomer = true;
              }
            }
            if (ts.vendorVerificationHours !== undefined) {
              this.vendorVerificationHours = ts.vendorVerificationHours;
            }
          }
          if (rtData.uploadedTimesheetUrl) {
            this.uploadedTimesheetUrl = rtData.uploadedTimesheetUrl;
          }
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy() {
    this.rtSub?.unsubscribe();
  }

  async loadData() {
    this.isLoading = true;
    try {
      await Promise.all([this.loadTimesheet(), this.loadBranding()]);
    } catch (error) {
      console.error('Error loading invoice data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadTimesheet() {
    const tsRef = doc(
      this.afs,
      `booking-list/${this.data.bookingId}/timesheets/main`,
    );
    const snap = await getDoc(tsRef);
    if (snap.exists()) {
      const data = snap.data();
      const allRows = data['rows'] || [];
      const vendorRows = allRows.filter(
        (r: any) => (r.addedBy || 'Vendor') === 'Vendor',
      );
      const customerRows = allRows.filter(
        (r: any) => (r.addedBy || 'Vendor') === 'Customer',
      );

      if (vendorRows.length > 0) {
        this.timesheetRows = vendorRows;
        this.isFallbackToCustomer = false;
      } else if (customerRows.length > 0) {
        this.timesheetRows = customerRows;
        this.isFallbackToCustomer = true;
      }

      this.vendorVerificationHours = data['vendorVerificationHours'] || null;
      this.uploadedTimesheetUrl =
        data['uploadedTimesheetUrl'] || this.uploadedTimesheetUrl;
    }
  }

  async loadBranding() {
    // Fetch Vendor Branding
    const vendorId = this.data.booking.equipment?.bywhom;
    if (vendorId) {
      const vRef = doc(this.afs, `users-list/${vendorId}`);
      const vSnap = await getDoc(vRef);
      if (vSnap.exists()) {
        const d = vSnap.data();
        this.vendorBranding = {
          name:
            d['companyName'] ||
            d['displayName'] ||
            d['accountdetails']?.companyname ||
            'Vendor',
          logo: d['companyLogoUrl'] || d['accountdetails']?.logoUrl || null,
          logoBase64: d['companyLogoBase64'] || null, // Added
          email: d['email'] || d['accountdetails']?.emailid,
          phone: d['phone'] || d['accountdetails']?.phoneNumber,
          address: d['accountdetails']?.companyAddress || d['accountdetails']?.address || d['companyAddress'] || d['address'] || '',
        };
        
        // Prioritize existing Base64
        if (this.vendorBranding.logoBase64) {
          this.vendorLogoBase64 = this.vendorBranding.logoBase64;
        } else if (this.vendorBranding.logo) {
          this.makeImageCircular(this.vendorBranding.logo)
            .then(b64 => this.vendorLogoBase64 = b64)
            .catch(e => console.warn('Failed to load vendor logo for PDF', e));
        }
      }
    }

    // Fetch Customer Branding
    const customerId = this.data.booking.bywhom;
    if (customerId) {
      const cRef = doc(this.afs, `users-list/${customerId}`);
      const cSnap = await getDoc(cRef);
      if (cSnap.exists()) {
        const d = cSnap.data();
        this.customerBranding = {
          name:
            d['companyName'] ||
            d['displayName'] ||
            d['accountdetails']?.companyname ||
            'Customer',
          email: d['email'] || d['accountdetails']?.emailid,
          phone: d['phone'] || d['accountdetails']?.phoneNumber,
          address: d['companyAddress'] || d['address'] || d['location'] || '',
        };
      }
    }
  }

  getTotalHours(): number {
    if (
      this.vendorVerificationHours !== null &&
      this.vendorVerificationHours !== undefined
    ) {
      return this.vendorVerificationHours;
    }
    return this.timesheetRows.reduce((sum, r) => sum + (r.totalHours || 0), 0);
  }

  getBalanceDue(): number {
    const rate = this.data.booking.rate || 0;
    const priceType = (this.data.booking.priceType || '')
      .toLowerCase()
      .replace('_', ' ');

    // If we have hours, calculate based on hours
    const totalHours = this.getTotalHours();
    if (totalHours > 0) {
      if (priceType === 'daily') {
        return totalHours * (rate / 8);
      }
      if (priceType === 'hourly') {
        return totalHours * rate;
      }
    }

    return this.data.booking.totalAmount || 0;
  }

  getFinalTotal(): number {
    return this.getBalanceDue() + (Number(this.deliveryCharge) || 0) - (Number(this.discount) || 0);
  }

  async confirmAndSend() {
    if (!this.signatureUrl) {
      this.ux.showError('Please sign before confirming', 'Signature Required');
      return;
    }

    this.isLoading = true;
    try {
      const bookingRef = doc(this.afs, `booking-list/${this.data.bookingId}`);
      const invoiceData = {
        invoiceFinalized: true,
        vendorSignatureUrl: this.signatureUrl,
        vendorSignatureBase64: this.signatureBase64, // Added
        uploadedTimesheetUrl: this.uploadedTimesheetUrl || null,
        uploadedInvoiceUrl: this.uploadedInvoiceUrl || null,
        invoiceAmount: this.getBalanceDue(),
        invoicedAt: new Date(),
        invoiceNumber: this.invoiceNumber,
        deliveryCharge: Number(this.deliveryCharge) || 0,
        discount: Number(this.discount) || 0,
        totalInvoiceAmount: this.getFinalTotal(),
        quotationNumber: this.data.booking?.quotationNumber ?? null,
      };

      // 1. Update Firestore
      await updateDoc(bookingRef, invoiceData);
      this.isFinalized = true;

      // 2. Update RTDB
      await this.rtBooking.updateInvoice(
        this.data.bookingId,
        {
          finalized: true,
          vendorSig: this.signatureUrl,
          vendorSigBase64: this.signatureBase64, // Added
          url: this.uploadedInvoiceUrl || null,
          amount: invoiceData.invoiceAmount,
          number: this.invoiceNumber,
          invoicedAt: Date.now(),
          updatedAt: Date.now(),
        },
        this.auth.currentUser?.uid
      );

      // Update status to 'Completed' if we want auto-completion upon invoice
      // Actually, let's keep it 'Confirmed' until payment or manual completion?
      // In some systems, invoice means it's done.
      // await this.rtBooking.updateStatus(this.data.bookingId, 'Completed');

      // Notify Customer
      const customerId = this.data.booking.bywhom;
      const vendorName = this.vendorBranding?.name || 'Vendor';
      const amount = this.getBalanceDue();

      if (customerId) {
        this.notificationService.sendNotification(customerId, {
          title: 'New Invoice Received',
          message: `Invoice ${this.invoiceNumber} for AED ${amount} received from ${vendorName}`,
          bookingId: this.data.bookingId,
          type: 'info',
          link: `/booking-orders/order-details/${this.data.bookingId}`,
        });
      }

      this.ux.showSuccess(
        'Invoice confirmed and sent to customer!',
        'Invoice Sent',
      );
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error finalizing invoice:', error);
      this.ux.showError('Error finalizing invoice. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  // --- SIGNATURE METHODS ---
  triggerSigInput() {
    document.getElementById('invSigInput')?.click();
  }

  async onSigSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      try {
        // Convert to Base64 for instant PDF use
        const base64 = await this.fileToBase64(file);
        this.signatureBase64 = base64;

        const storage = getStorage();
        const path = `invoices/${this.data.bookingId}_sig_${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        const uploadResult = await uploadBytes(storageRef, file);
        this.signatureUrl = await getDownloadURL(uploadResult.ref);

        // Update RTDB immediately
        await this.rtBooking.updateInvoice(
          this.data.bookingId,
          {
            vendorSig: this.signatureUrl,
            vendorSigBase64: this.signatureBase64,
          },
          this.auth.currentUser?.uid
        );
      } catch (error) {
        console.error('Error uploading signature:', error);
      }
    }
  }

  initCanvas() {
    if (this.sigCanvas) {
      const canvas = this.sigCanvas.nativeElement;
      this.ctx = canvas.getContext('2d')!;
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = '#000000';
    }
  }

  startDrawing(event: MouseEvent) {
    if (!this.ctx) this.initCanvas();
    this.isDrawing = true;
    this.ctx.beginPath();
    this.ctx.moveTo(event.offsetX, event.offsetY);
  }

  draw(event: MouseEvent) {
    if (!this.isDrawing) return;
    this.ctx.lineTo(event.offsetX, event.offsetY);
    this.ctx.stroke();
  }

  stopDrawing() {
    this.isDrawing = false;
    this.ctx.closePath();
  }

  clearCanvas() {
    if (this.sigCanvas) {
      const canvas = this.sigCanvas.nativeElement;
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  async saveCanvasSignature() {
    if (!this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;
    
    // Save locally as Base64 first to ensure it's available for immediate PDF generation without CORS
    const base64 = canvas.toDataURL('image/png');
    this.signatureUrl = base64;
    this.signatureBase64 = base64;

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          const storage = getStorage();
          const path = `invoices/${this.data.bookingId}_drawn_${Date.now()}.png`;
          const storageRef = ref(storage, path);
          const uploadResult = await uploadBytes(storageRef, blob);
          this.signatureUrl = await getDownloadURL(uploadResult.ref);

          // Update RTDB immediately
          await this.rtBooking.updateInvoice(
            this.data.bookingId,
            {
              vendorSig: this.signatureUrl,
              vendorSigBase64: this.signatureBase64,
            },
            this.auth.currentUser?.uid
          );
        } catch (error) {
          console.error('Error saving drawn signature:', error);
        }
      }
    }, 'image/png');
  }

  // ==========================================
  // DOCUMENT UPLOADS (Timesheet & Invoice)
  // ==========================================

  openUploadDialog(type: 'invoice') {
    if (!this.canEditInvoice) return;

    const title = 'Upload Invoice';
    const existingUrl = this.uploadedInvoiceUrl;
    const storagePathBase = `documents/${this.data.bookingId}`;
    const filePrefix = 'invoice';

    const dialogRef = this.dialog.open(UploadDocumentDialogComponent, {
      width: '500px',
      data: { title, existingUrl, storagePathBase, filePrefix },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      // If result is present, it means a new file was uploaded
      if (result && result.uploadedUrl) {
        this.uploadedInvoiceUrl = result.uploadedUrl;
        // Update RTDB immediately
        await this.rtBooking.updateInvoice(
          this.data.bookingId,
          {
            url: this.uploadedInvoiceUrl,
          },
          this.auth.currentUser?.uid
        );

        // Also update Firestore main doc
        const bookingRef = doc(this.afs, `booking-list/${this.data.bookingId}`);
        await updateDoc(bookingRef, {
          uploadedInvoiceUrl: this.uploadedInvoiceUrl,
          quotationNumber: this.data.booking?.quotationNumber ?? null,
        });
      }
    });
  }

  isImage(url: string): boolean {
    if (!url) return false;
    const lowUrl = url.toLowerCase();
    return (
      lowUrl.includes('.png') ||
      lowUrl.includes('.jpg') ||
      lowUrl.includes('.jpeg') ||
      lowUrl.includes('.webp') ||
      lowUrl.includes('image')
    );
  }

  close() {
    this.dialogRef.close();
  }

  // Helper to convert URL to Base64 to bypass CORS in html2canvas
  async getBase64FromUrl(url: string): Promise<string | null> {
    try {
      // Append timestamp to bypass cache (handle existing query params)
      const separator = url.includes('?') ? '&' : '?';
      const res = await fetch(url + separator + 't=' + new Date().getTime());
      if (!res.ok) throw new Error(`Fetch failed: ${res.statusText}`);
      const blob = await res.blob();

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('Could not load image (CORS likely):', url, e);
      return null;
    }
  }

  // Helper to convert File to Base64
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  async downloadPdf() {
    this.isPdfGenerating = true;

    // 1. Pre-process images to Base64
    const originalLogo = this.vendorBranding.logo;
    const originalSig = this.signatureUrl;
    const originalTS = this.uploadedTimesheetUrl;

    try {
      if (this.signatureBase64) {
        this.signatureUrl = this.signatureBase64;
      } else if (this.signatureUrl && !this.signatureUrl.startsWith('data:')) {
        const b64 = await this.getBase64FromUrl(this.signatureUrl);
        if (b64) this.signatureUrl = b64;
      }

      if (this.vendorLogoBase64) {
          this.vendorBranding.logo = this.vendorLogoBase64;
      } else if (
        this.vendorBranding.logo &&
        !this.vendorBranding.logo.startsWith('data:')
      ) {
        const b64 = await this.getBase64FromUrl(this.vendorBranding.logo);
        if (b64) this.vendorBranding.logo = b64;
      }

      if (
        this.uploadedTimesheetUrl &&
        this.isImage(this.uploadedTimesheetUrl) &&
        !this.uploadedTimesheetUrl.startsWith('data:')
      ) {
        const b64 = await this.getBase64FromUrl(this.uploadedTimesheetUrl);
        if (b64) this.uploadedTimesheetUrl = b64;
      }

      // Wait for DOM to update with new Base64 srcs
      // Increased timeout to ensure rendering is complete
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // 2. Generate PDF
      const element = this.pdfPage1.nativeElement;
      const options = {
        scale: 2,
        useCORS: true,
        logging: true,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        onclone: (clonedDoc: Document) => {
          // Ensure the cloned element is fully expanded and visible for capture
          const clonedElement = clonedDoc.querySelector('.pdf-page-container') as HTMLElement;
          if (clonedElement) {
              clonedElement.style.height = 'auto';
              clonedElement.style.overflow = 'visible';
          }
        }
      };

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;

      console.log('Capturing invoice content...');
      const canvas = await html2canvas(element, options);
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate proportional dimensions to fit on a single page
      let imgWidth = pageWidth;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // If height exceeds page height, scale down further
      if (imgHeight > pageHeight) {
        imgHeight = pageHeight;
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }
      
      // Center horizontally on page
      const xOffset = (pageWidth - imgWidth) / 2;
      const yOffset = 0; // Top-aligned

      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight);

      console.log('Saving PDF...');
      pdf.save(`${this.invoiceNumber}.pdf`);
      this.ux.showSuccess('Invoice downloaded successfully!', 'Downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Could not generate PDF. Please try again.');
    } finally {
      // Restore original URLs to original object properties
      this.signatureUrl = originalSig;
      this.vendorBranding.logo = originalLogo;
      this.uploadedTimesheetUrl = originalTS;
      this.isPdfGenerating = false;
    }
  }

  async makeImageCircular(url: string): Promise<string> {
    if (!url) return '';
    if (url.startsWith('data:')) return url;

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = Math.min(img.width, img.height);
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;

        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(
          img,
          (img.width - size) / 2,
          (img.height - size) / 2,
          size,
          size,
          0,
          0,
          size,
          size,
        );
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => {
        // Fallback to simple base64
        this.getBase64FromUrl(url).then((b64) => resolve(b64 || ''));
      };

      const separator = url.includes('?') ? '&' : '?';
      img.src = url + separator + 'cb=' + Date.now();
    });
  }
}
